import { Button, Form, TimePicker } from "antd";
import { NamePath } from "antd/lib/form/interface";
import dayjs, { Dayjs } from "dayjs";
import moment, { Moment } from "moment";
import { forwardRef, useCallback, useRef, useState } from "react";
import {
  formatIsoInTokyo,
  isoToTokyoPickerDayjs,
  pickerTimeToLoggedAtIso,
  tokyoNowPickerDayjs,
  WORK_LOG_TIMEZONE,
} from "@lhd/features/TrackingTime/workLogTimezone";
import { Styles } from "./styled";

interface IProps {
  name: NamePath;
  emptyLabel?: string;
  editable?: boolean;
  disabled?: boolean;
  readonlyIso?: string | null;
  workDate?: string;
}

const parseToDayjs = (value: unknown): Dayjs | null => {
  if (value == null || value === "") return null;
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }
  const s = value as string;
  return isoToTokyoPickerDayjs(s);
};

const dayjsToMoment = (value: Dayjs | null | undefined): Moment | null => {
  if (value == null || !value.isValid()) return null;
  return moment(value.format("YYYY-MM-DD HH:mm:ss"), "YYYY-MM-DD HH:mm:ss");
};

const momentToDayjs = (value: Moment | null): Dayjs | null => {
  if (value == null || !value.isValid()) return null;
  return dayjs.tz(
    value.format("YYYY-MM-DD HH:mm"),
    "YYYY-MM-DD HH:mm",
    WORK_LOG_TIMEZONE,
  );
};

const formatDisplayTime = (iso?: string | null, emptyLabel?: string) => {
  const fallback = emptyLabel ?? "--:--";
  if (iso == null || iso === "") return fallback;
  const formatted = formatIsoInTokyo(iso, "HH:mm");
  return formatted || fallback;
};

type TimelineTimePickerProps = {
  emptyLabel: string;
  disabled: boolean;
  readonlyIso?: string | null;
  workDate?: string;
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
};

const TimelineTimePicker = forwardRef<
  unknown,
  TimelineTimePickerProps & Record<string, unknown>
>(({ emptyLabel, disabled, value, onChange, ...rest }, ref) => {
  const [open, setOpen] = useState(false);
  const pendingMomentRef = useRef<Moment | null>(null);

  const commitMoment = useCallback(
    (next: Moment | null) => {
      onChange?.(momentToDayjs(next));
    },
    [onChange],
  );

  const handlePickerChange = useCallback(
    (next: Moment | null) => {
      commitMoment(next);
      setOpen(false);
    },
    [commitMoment],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        pendingMomentRef.current = dayjsToMoment(value);
      }
    },
    [value],
  );

  const handleSelect = useCallback((next: Moment) => {
    pendingMomentRef.current = next;
  }, []);

  const handleTokyoNow = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const m = dayjsToMoment(tokyoNowPickerDayjs());
      if (!m) return;
      pendingMomentRef.current = m;
      commitMoment(m);
      setOpen(false);
    },
    [commitMoment],
  );

  const handleOk = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      commitMoment(pendingMomentRef.current);
      setOpen(false);
    },
    [commitMoment],
  );

  const renderFooter = useCallback(
    () => (
      <ul className="ant-picker-ranges timeline-time-ranges">
        <li className="ant-picker-now">
          <a
            className="ant-picker-now-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleTokyoNow}
          >
            現在時刻
          </a>
        </li>
        <li className="ant-picker-ok">
          <Button
            type="primary"
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleOk}
          >
            決定
          </Button>
        </li>
      </ul>
    ),
    [handleOk, handleTokyoNow],
  );

  return (
    <TimePicker
      {...rest}
      ref={ref}
      open={open}
      onOpenChange={handleOpenChange}
      value={dayjsToMoment(value)}
      onChange={handlePickerChange}
      onSelect={handleSelect}
      format="HH:mm"
      placeholder={emptyLabel}
      style={{ width: "100%", minWidth: 112 }}
      allowClear={false}
      disabled={disabled}
      showNow={false}
      popupClassName="ant-picker-dropdown--timeline"
      renderExtraFooter={renderFooter}
    />
  );
});

TimelineTimePicker.displayName = "TimelineTimePicker";

const TimeField = (props: IProps) => {
  const {
    name,
    emptyLabel = "--:--",
    editable = true,
    disabled = false,
    readonlyIso,
    workDate,
  } = props;

  if (!editable) {
    return (
      <Styles className="timeline-time timeline-time-readonly">
        <span className="readonly-line">
          {formatDisplayTime(readonlyIso, emptyLabel)}
        </span>
      </Styles>
    );
  }

  return (
    <Styles className="timeline-time">
      <Form.Item
        name={name}
        getValueProps={(fieldValue) => ({
          value: parseToDayjs(fieldValue),
        })}
        getValueFromEvent={(v: Dayjs | null) =>
          v && v.isValid()
            ? pickerTimeToLoggedAtIso(v, { readonlyIso, workDate })
            : null
        }
      >
        <TimelineTimePicker
          emptyLabel={emptyLabel}
          disabled={disabled}
          readonlyIso={readonlyIso}
          workDate={workDate}
        />
      </Form.Item>
    </Styles>
  );
};

export default TimeField;
